import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

//lib

//redux
import {
  getProductList,
  getTopSearch,
  setFilterData,
} from "../../../redux/reducer/product.reducer";
import { useDispatch, useSelector } from "react-redux";
import { setSavedProduct } from "../../../redux/reducer/product.reducer";

//component
import HomeBanner from "./home.banner";
import Col from "../../../components/element/col";
import Row from "../../../components/element/row";
import Container from "../../../components/element/container";
import CommonProduct from "../../../components/common/common.product";
import useWindowDimension from "../../../components/hook/use.window.dimension";
import HomeFilteredValues from "./home.filtered.values";
import Button from "../../../components/element/button";
import Icon from "../../../components/element/icon";

const PRODUCT_PAGE_SIZE = 9;
const EMPTY_PRODUCTS = [];

export default function HomeProduct({
  showBanner = true,
}) {
  const { t } = useTranslation();
  const filterDataRef = useRef({});
  const previousFilterDataRef = useRef(null);
  const listRequestRef = useRef(0);
  const singleCategoryResponseRef = useRef(false);

  const dispatch = useDispatch();
  const { width } = useWindowDimension();
  const { productList, productListLoading, filterData } = useSelector(
    (state) => state.product,
  );

  const category = filterData.category || "all";
  const categoryData = filterData[category] || {};
  const pageStart = categoryData.start || 0;
  filterDataRef.current = filterData;

  useEffect(() => {
    const requestFilterData = filterDataRef.current[category] || {};
    const previousFilterData = previousFilterDataRef.current;
    const previousCategoryData = previousFilterData?.[category] || {};

    // Older responses contain all category arrays; the current API returns
    // products for just the requested category, so category changes must fetch.
    if (
      !singleCategoryResponseRef.current &&
      previousFilterData &&
      category !== (previousFilterData.category || "all") &&
      pageStart === (previousCategoryData.start || 0) &&
      requestFilterData.sort === previousCategoryData.sort &&
      requestFilterData.refresh_key === previousCategoryData.refresh_key
    ) return;

    const requestId = ++listRequestRef.current;

    dispatch(
      getProductList({
        ...requestFilterData,
        start: pageStart,
        length: PRODUCT_PAGE_SIZE,
        category: category === "all" ? "" : category,
      }),
    )
      .unwrap()
      .then((res) => {
        if (requestId !== listRequestRef.current) return;

        const currentFilterData = filterDataRef.current;
        const nextFilterData = { ...currentFilterData };
        singleCategoryResponseRef.current = Array.isArray(res.data.products);

        const categoryResults = singleCategoryResponseRef.current
          ? { [category]: res.data.products }
          : res.data;

        Object.entries(categoryResults).forEach(([key, value]) => {
          // skip non-array fields like totalFiltered
          if (!Array.isArray(value)) return;

          nextFilterData[key] = {
            ...(currentFilterData[key] || {}),
            data: value,
            start: pageStart,
            has_more:
              (res.data.category_totals?.[key] || 0) > pageStart + value.length,
          };
        });

        // A category may have changed before the initial response arrived.
        const currentCategory = currentFilterData.category || "all";
        if (singleCategoryResponseRef.current && currentCategory !== category) {
          nextFilterData[currentCategory] = {
            ...(currentFilterData[currentCategory] || {}),
            refresh_key: Date.now(),
          };
        }

        dispatch(setFilterData(nextFilterData));
      })
      .catch((ex) => {});
  }, [
    category,
    categoryData.refresh_key,
    categoryData.sort,
    pageStart,
    dispatch,
  ]);

  useEffect(() => {
    previousFilterDataRef.current = filterData;
  });

  useEffect(() => {
    dispatch(getTopSearch());
  }, [dispatch]);

  const list = categoryData?.data || EMPTY_PRODUCTS;
  // const featured = list.slice(0, 3);
  // const other = list.slice(3);
  const other = list;
  const masonryColumnCount =
    width >= 1100 ? 3 : (width > 768 && width <= 820) || width < 550 ? 1 : 2;
  const initialSkeletons = Array.from(
    { length: PRODUCT_PAGE_SIZE },
    (_, index) => ({
      id: `initial-skeleton-${index}`,
    }),
  );
  const totalProducts =
    productList?.category_totals?.[category] ??
    productList?.totalFiltered ??
    list.length;
  const pageCount = Math.ceil(totalProducts / PRODUCT_PAGE_SIZE);
  const currentPage = Math.floor(pageStart / PRODUCT_PAGE_SIZE) + 1;
  const firstVisiblePage = Math.max(
    1,
    Math.min(currentPage - 2, pageCount - 4),
  );
  const visiblePages = Array.from(
    { length: Math.min(5, pageCount) },
    (_, index) => firstVisiblePage + index,
  );

  const handlePageChange = (page) => {
    if (page < 1 || page > pageCount || page === currentPage) return;

    dispatch(
      setFilterData({
        ...filterData,
        [category]: {
          ...categoryData,
          start: (page - 1) * PRODUCT_PAGE_SIZE,
        },
      }),
    );

    document.getElementById("home-product-list")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const buildColumns = (products) => {
    const columns = Array.from({ length: masonryColumnCount }, () => []);

    products.forEach((product, index) => {
      columns[index % masonryColumnCount].push(product);
    });

    return columns;
  };

  // const featuredColumns = buildColumns(featured);
  const otherColumns = buildColumns(other);

  useEffect(() => {
    if (list?.length > 0) {
      const savedIds = []
        .concat(list ?? [], productList?.discounted_products ?? [])
        .filter((product) => product.is_saved)
        .map((product) => product.id);

      dispatch(setSavedProduct(savedIds));
    }
  }, [dispatch, list, productList?.discounted_products]);

  const renderProductColumns = (
    columns,
    prefix,
    isLoading = false,
  ) => {
    return (
      <Row className="_row product-list">
        {columns.map((columnProducts, columnIndex) => (
          <Col
            className="_col product-column"
            key={`${prefix}-col-${columnIndex}`}
          >
            {columnProducts.map((product) => {
              return (
                <CommonProduct
                  login
                  product={isLoading ? undefined : product}
                  key={`${prefix}-${product.id}`}
                  loading={isLoading}
                  type="list"
                />
              );
            })}
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Container className="home-product" id="home-product-list">
      <HomeFilteredValues sticky={width >= 768} showCount />
      <Container className="product-list-wrapper">
        {productListLoading ? (
          renderProductColumns(
            buildColumns(initialSkeletons),
            "initial-skeleton",
            true,
          )
        ) : Array.isArray(categoryData.data) && list.length === 0 ? (
          <Col className="_col home-product-empty">
            <Icon icon="solar:garage-linear" className="_svg home-product-empty" />
            <p className="_label home-product-empty">{t("No car found")}</p>
          </Col>
        ) : (
          <>
            {width >= 768 ? (
              renderProductColumns(buildColumns(list), "masonry", false)
            ) : (
              <>
                {/* {renderProductColumns(featuredColumns, "featured", false, {
                  firstAsReserver: showReserver,
                })} */}

                {showBanner && category !== "car plate" && list?.length > 3 && (
                  <HomeBanner />
                )}

                {other.length > 0 &&
                  renderProductColumns(otherColumns, "prest")}
              </>
            )}
          </>
        )}
      </Container>
      {!productListLoading && pageCount > 1 && (
        <Row className="_row product-pagination">
          <Button
            className="_button pagination-control"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <Icon icon="iconamoon:arrow-left-2" className="_svg pagination" />
          </Button>
          {visiblePages.map((page) => (
            <Button
              className={`_button pagination-page ${
                page === currentPage ? "active" : ""
              }`}
              key={page}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </Button>
          ))}
          <Button
            className="_button pagination-control"
            disabled={currentPage === pageCount}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <Icon icon="iconamoon:arrow-right-2" className="_svg pagination" />
          </Button>
        </Row>
      )}
    </Container>
  );
}
